import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CryptoJS from "crypto-js";

// Adjust this import based on how you export from the library:
// If you did `export { CygnusForgotPassword } from "./components/...";` in index.ts:
import {CygnusForgotPassword} from "@cygnusaiinternal/cygnus-common"; 

interface ForgotPasswordRequestResult {
  success: boolean;
  message?: string;
}

interface ForgotPasswordVerifyResult {
  success: boolean;
  message?: string;
}

interface ForgotPasswordResetResult {
  success: boolean;
  message?: string;
}

/**
 * ForgotPasswordPage
 * Thin wrapper that wires app-specific API + toast + navigation
 * into the reusable CygnusForgotPassword component from the library.
 */
const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URI;

  const handleRequestReset = useCallback(
    async ({ email }: { email: string }): Promise<ForgotPasswordRequestResult> => {
      if (!email.trim()) {
        const message = "Email cannot be empty.";
        toast.error(message);
        return { success: false, message };
      }

      try {
        const url = `${baseUrl}/workflow/request_otp/`;
        const response = await fetch(url, {
          method: "POST",
          body: JSON.stringify({ email }),
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
          },
        });

        const data = await response.json();

        if (data.success) {
          toast.success("Confirmation email sent! Please check your inbox.");
          return { success: true, message: data.message };
        }

        const message = data.message || "An error occurred. Please try again.";
        toast.error(message);
        return { success: false, message };
      } catch (error: any) {
        const message =
          error?.message || "An error occurred. Please try again.";
        toast.error(message);
        return { success: false, message };
      }
    },
    [baseUrl]
  );

  const handleVerifyOtp = useCallback(
    async ({
      email,
      otp,
    }: {
      email: string;
      otp: string;
    }): Promise<ForgotPasswordVerifyResult> => {
      if (!otp.trim()) {
        const message = "OTP cannot be empty.";
        toast.error(message);
        return { success: false, message };
      }

      try {
        const url = `${baseUrl}/workflow/verify_otp/`;
        const response = await fetch(url, {
          method: "POST",
          body: JSON.stringify({ email, otp }),
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
          },
        });

        const data = await response.json();

        if (data.success) {
          toast.success("OTP verified! Please set your new password.");
          return { success: true, message: data.message };
        }

        if (response.status === 403) {
          const message = data.message || "Invalid OTP. Please try again.";
          toast.error(message);
          navigate("/login");
          return { success: false, message };
        }

        const message = data.message || "Invalid OTP. Please try again.";
        toast.error(message);
        return { success: false, message };
      } catch (error: any) {
        const message =
          error?.message || "An error occurred. Please try again.";
        toast.error(message);
        return { success: false, message };
      }
    },
    [baseUrl, navigate]
  );

  const handleResetPassword = useCallback(
    async ({
      email,
      otp,
      newPassword,
    }: {
      email: string;
      otp: string;
      newPassword: string;
    }): Promise<ForgotPasswordResetResult> => {
      try {
        const url = `${baseUrl}/workflow/reset_password/`;
        const hashedPassword = CryptoJS.SHA256(newPassword).toString();

        const response = await fetch(url, {
          method: "POST",
          body: JSON.stringify({
            email,
            otp,
            password: hashedPassword,
          }),
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
          },
        });

        const data = await response.json();

        if (data.success) {
          const message =
            data.message || "Password updated successfully! Please log in.";
          toast.success(message);
          return { success: true, message };
        }

        const message = data.message || "An error occurred. Please try again.";
        toast.error(message);
        return { success: false, message };
      } catch (error: any) {
        const message =
          error?.message || "An error occurred. Please try again.";
        toast.error(message);
        return { success: false, message };
      }
    },
    [baseUrl]
  );

  return (
    <CygnusForgotPassword
      onRequestReset={handleRequestReset}
      onVerifyOtp={handleVerifyOtp}
      onResetPassword={handleResetPassword}
      onCompleted={() => {
        // Called after successful reset inside the library component
        navigate("/login");
      }}
      onBackToLogin={() => navigate("/login")}
      // Optional branding props
      titleTextStep1="Forgot Password"
      titleTextStep2="Enter OTP"
      titleTextStep3="Set New Password"
      titleText="CYGNUS ACCOUNTING HUB"
      // logoSrc={require("../path/to/logo.png")} // add if you want logo
    />
  );
};

export default ForgotPasswordPage;
